'use client';

import { useCallback, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation';
import { useApexCalendarFilters } from './ApexCalendarFiltersProvider';
import { useClickOutside } from './useClickOutside';
import {
  SelectorActions,
  SelectorActionButton,
  SelectorBodyCheckRow,
  GroupLabel,
  SelectorBodyInner,
  SelectorBodyWithGap,
  SelectorHeader,
  SelectorLabel,
  SelectorRow,
  SelectorValue,
  StyledCheckbox,
} from './apexCalendarSelectors.styles';

const FLIGHT_TIMES = [
  { value: 1, key: 'vacation.flightavailability.filters.timeslot.morning' },
  { value: 2, key: 'vacation.flightavailability.filters.timeslot.afternoon' },
  { value: 3, key: 'vacation.flightavailability.filters.timeslot.evening' },
] as const;

function toggleValue(arr: number[], value: number): number[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

function buildSummary(selected: number[], labels: string[], anyLabel: string): string {
  if (selected.length === 0) return anyLabel;
  return selected.map((v) => labels[v - 1]).join(', ');
}

export function TravelTimesSelector() {
  const { t } = useTranslation();
  const { flightTimesOutbound, flightTimesInbound, setFlightTimesOutbound, setFlightTimesInbound } =
    useApexCalendarFilters();
  const [isOpen, setIsOpen] = useState(false);
  const [draftFlightTimesOutbound, setDraftTransportTimesOutbound] = useState<number[]>(flightTimesOutbound);
  const [draftFlightTimesInbound, setDraftTransportTimesInbound] = useState<number[]>(flightTimesInbound);

  const handleCancel = useCallback(() => {
    setDraftTransportTimesOutbound(flightTimesOutbound);
    setDraftTransportTimesInbound(flightTimesInbound);
    setIsOpen(false);
  }, [flightTimesInbound, flightTimesOutbound]);

  const selectorRef = useClickOutside<HTMLDivElement>(handleCancel);

  const anyLabel = t('vacation.select.evo_price_calendar.anytime') || t('vacation.oss.header.widget.calendar.anytime');
  const timeLabels = FLIGHT_TIMES.map(({ key }) => t(key));
  const outboundSummary = buildSummary(flightTimesOutbound, timeLabels, anyLabel);
  const inboundSummary = buildSummary(flightTimesInbound, timeLabels, anyLabel);
  const valueSummary =
    flightTimesOutbound.length === 0 && flightTimesInbound.length === 0
      ? anyLabel
      : `${outboundSummary} / ${inboundSummary}`;

  const toggleSelectorOpen = () => {
    if (!isOpen) {
      setDraftTransportTimesOutbound(flightTimesOutbound);
      setDraftTransportTimesInbound(flightTimesInbound);
    }
    setIsOpen((open) => !open);
  };

  const handleDone = () => {
    setFlightTimesOutbound(draftFlightTimesOutbound);
    setFlightTimesInbound(draftFlightTimesInbound);
    setIsOpen(false);
  };

  return (
    <SelectorRow ref={selectorRef}>
      <SelectorHeader $isOpen={isOpen} onClick={toggleSelectorOpen}>
        <SelectorLabel>
          {t('vacation.select.evo_price_calendar.flight_times') || t('vacation.flightavailability.filters.traveltimes')}
        </SelectorLabel>
        <SelectorValue>{valueSummary}</SelectorValue>
      </SelectorHeader>

      {isOpen && (
        <SelectorBodyWithGap>
          <SelectorBodyInner>
            <GroupLabel>{t('vacation.select.evo_price_calendar.outbound_flight_times') || 'Departure'}</GroupLabel>
            {FLIGHT_TIMES.map(({ value, key }) => (
              <SelectorBodyCheckRow key={value}>
                <StyledCheckbox
                  checkbox={{ id: `outbound-${value}`, label: t(key) }}
                  labelPosition="end"
                  isChecked={draftFlightTimesOutbound.includes(value)}
                  onChange={() => setDraftTransportTimesOutbound(toggleValue(draftFlightTimesOutbound, value))}
                />
              </SelectorBodyCheckRow>
            ))}
          </SelectorBodyInner>
          <SelectorBodyInner>
            <GroupLabel>{t('vacation.select.evo_price_calendar.inbound_flight_times') || 'Return'}</GroupLabel>
            {FLIGHT_TIMES.map(({ value, key }) => (
              <SelectorBodyCheckRow key={value}>
                <StyledCheckbox
                  checkbox={{ id: `inbound-${value}`, label: t(key) }}
                  labelPosition="end"
                  isChecked={draftFlightTimesInbound.includes(value)}
                  onChange={() => setDraftTransportTimesInbound(toggleValue(draftFlightTimesInbound, value))}
                />
              </SelectorBodyCheckRow>
            ))}
          </SelectorBodyInner>
          <SelectorActions>
            <SelectorActionButton variant="primary" appearance="ghost" onClick={handleCancel}>
              {t('vacation.oss.components.peopleselector.cancel')}
            </SelectorActionButton>
            <SelectorActionButton variant="primary" onClick={handleDone}>
              {t('vacation.oss.components.peopleselector.done')}
            </SelectorActionButton>
          </SelectorActions>
        </SelectorBodyWithGap>
      )}
    </SelectorRow>
  );
}
